import { timeSlotParse } from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour.js';
import { utcToZonedTime } from 'date-fns-tz';
import { generateRelativeTime, isTimeAfter } from 'shared/helpers/DateHelper';

const MINUTE_ROUNDING_FACTOR = 5;

export default {
  data() {
    return {
      dayNames: [
        this.$t('DAY_NAMES.SUNDAY'),
        this.$t('DAY_NAMES.MONDAY'),
        this.$t('DAY_NAMES.TUESDAY'),
        this.$t('DAY_NAMES.WEDNESDAY'),
        this.$t('DAY_NAMES.THURSDAY'),
        this.$t('DAY_NAMES.FRIDAY'),
        this.$t('DAY_NAMES.SATURDAY'),
      ],
      timeSlots: [],
      timeSlot: null,
    };
  },
  computed: {
    channelConfig() {
      return window.aiccWebChannel;
    },
    workingHours() {
      return this.channelConfig.workingHours || [];
    },
    holidays() {
      return this.channelConfig.holidays || [];
    },
    newDateWithTimeZone() {
      return utcToZonedTime(new Date(), this.timeZoneValue);
    },
    presentHour() {
      return this.newDateWithTimeZone.getHours();
    },
    presentMinute() {
      return this.newDateWithTimeZone.getMinutes();
    },
    currentDay() {
      return this.newDateWithTimeZone.getDay();
    },
    timeZoneValue() {
      return this.channelConfig.timezone;
    },
    languageCode() {
      return window.aiccWebChannel.locale;
    },
    isHoliday() {
      const today = this.newDateWithTimeZone;
      return this.holidays.some(holiday => {
        const holidayDate = new Date(holiday.date);
        return (
          holidayDate.getFullYear() === today.getFullYear() &&
          holidayDate.getMonth() === today.getMonth() &&
          holidayDate.getDate() === today.getDate()
        );
      });
    },
    currentDayWorkingHours() {
      if (this.isHoliday) {
        return null;
      }

      const daySlots = this.workingHours.filter(
        slot => slot.day_of_week === this.currentDay
      );

      if (!daySlots.length) {
        return null;
      }

      // If any slot is open all day, return that slot
      const openAllDaySlot = daySlots.find(slot => slot.open_all_day);
      if (openAllDaySlot) {
        return openAllDaySlot;
      }

      // If all slots are closed, return null
      if (daySlots.every(slot => slot.closed_all_day)) {
        return null;
      }

      // Get the next available slot
      const nextSlot = daySlots.find(slot => {
        if (slot.closed_all_day) return false;
        return isTimeAfter(
          slot.open_hour,
          slot.open_minutes,
          this.presentHour,
          this.presentMinute
        );
      });

      return nextSlot || daySlots[0];
    },
    nextDayWorkingHours() {
      let nextDay = this.getNextDay(this.currentDay);
      let nextWorkingHour = this.getNextWorkingHour(nextDay);

      // It gets the next working hour for the next day. If there is no working hour for the next day,
      // it keeps iterating through the days of the week until it finds the next working hour.
      while (!nextWorkingHour) {
        nextDay = this.getNextDay(nextDay);
        nextWorkingHour = this.getNextWorkingHour(nextDay);
      }
      return nextWorkingHour;
    },
    currentDayTimings() {
      const {
        open_hour: openHour,
        open_minutes: openMinute,
        close_hour: closeHour,
      } = this.currentDayWorkingHours ?? {};
      return {
        openHour,
        openMinute,
        closeHour,
      };
    },
    nextDayTimings() {
      const { open_hour: openHour, open_minutes: openMinute } =
        this.nextDayWorkingHours ?? {};
      return {
        openHour,
        openMinute,
      };
    },
    dayDiff() {
      const nextDay = this.nextDayWorkingHours.day_of_week;
      const totalDays = 6;
      return nextDay > this.currentDay
        ? nextDay - this.currentDay - 1
        : totalDays - this.currentDay + nextDay;
    },
    dayNameOfNextWorkingDay() {
      return this.dayNames[this.nextDayWorkingHours.day_of_week];
    },
    hoursAndMinutesBackInOnline() {
      if (this.isHoliday) {
        return this.getHoursAndMinutesUntilNextDayOpen(
          this.nextDayWorkingHours.open_all_day
            ? 0
            : this.nextDayTimings.openHour,
          this.nextDayTimings.openMinute,
          23
        );
      }

      if (this.presentHour >= this.currentDayTimings.closeHour) {
        return this.getHoursAndMinutesUntilNextDayOpen(
          this.nextDayWorkingHours.open_all_day
            ? 0
            : this.nextDayTimings.openHour,
          this.nextDayTimings.openMinute,
          this.currentDayTimings.closeHour
        );
      }

      return this.getHoursAndMinutesUntilNextDayOpen(
        this.currentDayTimings.openHour,
        this.currentDayTimings.openMinute,
        this.currentDayTimings.closeHour
      );
    },
    exactTimeInAmPm() {
      return `${this.timeSlot.day === this.currentDay ? `at ${this.timeSlot.from}` : ''}`;
    },
    hoursAndMinutesLeft() {
      const { hoursLeft, minutesLeft } = this.hoursAndMinutesBackInOnline;

      const timeLeftChars = [];

      if (hoursLeft > 0) {
        const roundedUpHoursLeft = minutesLeft > 0 ? hoursLeft + 1 : hoursLeft;
        const hourRelative = generateRelativeTime(
          roundedUpHoursLeft,
          'hour',
          this.languageCode
        );
        timeLeftChars.push(`${hourRelative}`);
      }

      if (minutesLeft > 0 && hoursLeft === 0) {
        const roundedUpMinLeft =
          Math.ceil(minutesLeft / MINUTE_ROUNDING_FACTOR) *
          MINUTE_ROUNDING_FACTOR;
        const minRelative = generateRelativeTime(
          roundedUpMinLeft,
          'minutes',
          this.languageCode
        );
        timeLeftChars.push(`${minRelative}`);
      }

      return timeLeftChars.join(' ');
    },
    hoursAndMinutesToBack() {
      const { hoursLeft, minutesLeft } = this.hoursAndMinutesBackInOnline;
      if (hoursLeft >= 3) {
        return this.exactTimeInAmPm;
      }
      if (hoursLeft > 0 || minutesLeft > 0) {
        return this.hoursAndMinutesLeft;
      }
      return 'in some time';
    },
    timeLeftToBackInOnline() {
      if (this.isHoliday) {
        return `on ${this.dayNameOfNextWorkingDay}`;
      }

      if (
        this.hoursAndMinutesBackInOnline.hoursLeft >= 24 ||
        (this.timeSlot?.day !== this.currentDay && this.dayDiff === 0)
      ) {
        const hourRelative = generateRelativeTime(
          this.dayDiff + 1,
          'days',
          this.languageCode
        );
        return `${hourRelative}`;
      }

      if (
        this.dayDiff >= 1 &&
        this.presentHour >= this.currentDayTimings.closeHour
      ) {
        return `on ${this.dayNameOfNextWorkingDay}`;
      }

      return this.hoursAndMinutesToBack;
    },
  },
  mounted() {
    this.setTimeSlot();
  },
  methods: {
    getNextDay(day) {
      return (day + 1) % 7;
    },
    getNextWorkingHour(day) {
      const daySlots = this.workingHours.filter(
        slot => slot.day_of_week === day
      );

      if (!daySlots.length) {
        return null;
      }

      // Check if the next day is a holiday
      const nextDate = new Date(this.newDateWithTimeZone);
      nextDate.setDate(nextDate.getDate() + 1);
      const isNextDayHoliday = this.holidays.some(holiday => {
        const holidayDate = new Date(holiday.date);
        return (
          holidayDate.getFullYear() === nextDate.getFullYear() &&
          holidayDate.getMonth() === nextDate.getMonth() &&
          holidayDate.getDate() === nextDate.getDate()
        );
      });

      if (isNextDayHoliday) {
        return null;
      }

      // If any slot is open all day, return that slot
      const openAllDaySlot = daySlots.find(slot => slot.open_all_day);
      if (openAllDaySlot) {
        return openAllDaySlot;
      }

      // If all slots are closed, return null
      if (daySlots.every(slot => slot.closed_all_day)) {
        return null;
      }

      // Return the first available slot
      return daySlots.find(slot => !slot.closed_all_day) || null;
    },
    getHoursAndMinutesUntilNextDayOpen(openHour, openMinutes, closeHour) {
      if (closeHour < openHour) {
        openHour += 24;
      }
      let diffMinutes =
        openHour * 60 +
        openMinutes -
        (this.presentHour * 60 + this.presentMinute);
      diffMinutes = diffMinutes < 0 ? diffMinutes + 24 * 60 : diffMinutes;
      const [hoursLeft, minutesLeft] = [
        Math.floor(diffMinutes / 60),
        diffMinutes % 60,
      ];

      return { hoursLeft, minutesLeft };
    },
    setTimeSlot() {
      const timeSlots = this.workingHours;

      // If the present hour is after the closing hour of the current day,
      // then the next day's working hours will be used to calculate the time remaining until the next day's opening hours,
      // else the current day's working hours will be used
      const currentSlot =
        this.presentHour >= this.currentDayTimings.closeHour
          ? this.nextDayWorkingHours
          : this.currentDayWorkingHours;

      // It parses the working hours to get the time slots in AM/PM format.
      const slots = timeSlotParse(timeSlots).length
        ? timeSlotParse(timeSlots)
        : [];
      this.timeSlots = slots;

      // It finds the time slot for the current slot.
      if (!currentSlot || !slots.length) {
        // If no current slot is found or no working hours are set, return null
        this.timeSlot = null;
      } else {
        this.timeSlot = this.timeSlots.find(
          slot => slot.day === currentSlot.day_of_week
        ) || {
          day: currentSlot.day_of_week,
          from: this.formatTime(
            currentSlot.open_hour,
            currentSlot.open_minutes
          ),
          to: this.formatTime(
            currentSlot.close_hour,
            currentSlot.close_minutes
          ),
        };
      }
    },
  },
};
